import React, { useState, useEffect, useMemo } from "react";
import { useAuth } from "../context/AuthContext";
import { commentsAPI, roomsAPI } from "../services/api";
import toast from "react-hot-toast";
import { Comment, CommentBookingRef } from "../types/comment";
import Pagination from "../components/ui/Pagination";
import { localDateStr } from "../utils/date";

const PAGE_SIZE = 10;

const SOURCE_LABEL: Record<string, string> = {
  zalo: "Zalo",
  facebook: "Facebook",
  instagram: "Instagram",
  google: "Google",
  other: "Nguồn khác",
};

interface ImportForm {
  roomId: string;
  userName: string;
  rating: number;
  source: string;
  date: string;
  comment: string;
}

const ANON_NAME = "Khách ẩn danh";
const EMPTY_IMPORT: ImportForm = { roomId: "", userName: "", rating: 5, source: "zalo", date: "", comment: "" };

function CommentsPage(): React.ReactElement {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === "superadmin";

  const [comments, setComments] = useState<Comment[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [selectedComment, setSelectedComment] = useState<Comment | null>(null);
  const [showDetailModal, setShowDetailModal] = useState<boolean>(false);
  const [replyText, setReplyText] = useState<string>("");
  const [filterStatus, setFilterStatus] = useState<string>("all");
  const [filterRating, setFilterRating] = useState<string>("all");
  const [filterBookingLink, setFilterBookingLink] = useState<string>("all");
  const [search, setSearch] = useState<string>("");
  const [page, setPage] = useState(1);
  const [showImport, setShowImport] = useState<boolean>(false);
  const [importForm, setImportForm] = useState<ImportForm>(EMPTY_IMPORT);
  const [importRooms, setImportRooms] = useState<{ _id: string; name: string }[]>([]);
  const [importing, setImporting] = useState<boolean>(false);
  const [bulkMode, setBulkMode] = useState<boolean>(false);
  const [bulkText, setBulkText] = useState<string>("");

  // Fetch comments from API
  useEffect(() => {
    fetchComments();
  }, []);

  const fetchComments = async (): Promise<void> => {
    try {
      const response = await commentsAPI.getAll();
      setComments(response.data.data || []);
    } catch (error) {
      console.error("Error fetching comments:", error);
      toast.error("Lỗi khi tải bình luận");
    } finally {
      setLoading(false);
    }
  };

  // Mở form nhập đánh giá thật từ Zalo/Facebook/Google
  const openImport = async (): Promise<void> => {
    setImportForm(EMPTY_IMPORT);
    setBulkMode(false);
    setBulkText("");
    setShowImport(true);
    if (importRooms.length === 0) {
      try {
        const res = await roomsAPI.getAll();
        setImportRooms(res.data.data || []);
      } catch {
        toast.error("Không tải được danh sách phòng");
      }
    }
  };

  const submitImport = async (): Promise<void> => {
    if (!importForm.roomId) return void toast.error("Vui lòng chọn phòng");
    if (!importForm.userName.trim()) return void toast.error("Vui lòng nhập tên hiển thị");
    if (importForm.comment.trim().length < 10) return void toast.error("Nội dung quá ngắn (tối thiểu 10 ký tự)");
    setImporting(true);
    try {
      await commentsAPI.importOne({
        roomId: importForm.roomId,
        userName: importForm.userName.trim(),
        rating: importForm.rating,
        source: importForm.source,
        comment: importForm.comment.trim(),
        ...(importForm.date ? { createdAt: importForm.date } : {}),
      });
      toast.success("Đã thêm đánh giá");
      setShowImport(false);
      await fetchComments();
    } catch (error) {
      const msg = (error as { response?: { data?: { message?: string } } }).response?.data?.message;
      toast.error(msg || "Lỗi khi thêm đánh giá");
    } finally {
      setImporting(false);
    }
  };

  // Nhập nhiều: mỗi dòng một đánh giá, dùng chung phòng / nguồn / số sao / tên hiển thị (mặc định ẩn danh)
  const bulkLines = bulkText
    .split("\n")
    .map((l) => l.trim())
    .filter(Boolean);
  const bulkValid = bulkLines.filter((l) => l.length >= 10 && l.length <= 1500);

  const submitBulk = async (): Promise<void> => {
    if (!importForm.roomId) return void toast.error("Vui lòng chọn phòng");
    if (bulkValid.length === 0) return void toast.error("Chưa có dòng nào hợp lệ (mỗi dòng tối thiểu 10 ký tự)");
    setImporting(true);
    let ok = 0;
    let failed = 0;
    for (const line of bulkValid) {
      try {
        await commentsAPI.importOne({
          roomId: importForm.roomId,
          userName: importForm.userName.trim() || ANON_NAME,
          rating: importForm.rating,
          source: importForm.source,
          comment: line,
        });
        ok++;
      } catch {
        failed++;
      }
    }
    setImporting(false);
    if (ok > 0) {
      toast.success(`Đã thêm ${ok} đánh giá${failed ? `, ${failed} dòng lỗi` : ""}`);
      setShowImport(false);
      await fetchComments();
    } else {
      toast.error("Không thêm được đánh giá nào");
    }
  };

  // Open detail modal
  const openDetail = (comment: Comment): void => {
    setSelectedComment(comment);
    setReplyText(comment.adminReply || "");
    setShowDetailModal(true);
  };

  // Update comment status
  const updateStatus = async (id: string, newStatus: string): Promise<void> => {
    try {
      await commentsAPI.updateStatus(id, newStatus);
      setComments(
        comments.map((comment) =>
          comment._id === id ? { ...comment, status: newStatus as Comment["status"] } : comment,
        ),
      );
      toast.success("Cập nhật trạng thái thành công");
    } catch (error) {
      toast.error("Lỗi khi cập nhật trạng thái");
    }
  };

  // Save reply
  const saveReply = async (): Promise<void> => {
    if (selectedComment) {
      try {
        await commentsAPI.reply(selectedComment._id, replyText);
        setComments(
          comments.map((comment) =>
            comment._id === selectedComment._id
              ? { ...comment, adminReply: replyText, status: comment.status === "pending" ? "approved" : comment.status }
              : comment,
          ),
        );
        setShowDetailModal(false);
        toast.success("Phản hồi thành công");
      } catch (error) {
        toast.error("Lỗi khi gửi phản hồi");
      }
    }
  };

  // Delete comment - superadmin only
  const deleteComment = async (id: string): Promise<void> => {
    if (window.confirm("Bạn có chắc muốn xóa bình luận này?")) {
      try {
        await commentsAPI.delete(id);
        setComments(comments.filter((c) => c._id !== id));
        toast.success("Đã xóa bình luận");
      } catch (error) {
        toast.error("Lỗi khi xóa bình luận");
      }
    }
  };

  const filteredComments = useMemo(() => {
    return comments.filter((comment) => {
      const statusMatch = filterStatus === "all" || comment.status === filterStatus;
      const ratingMatch = filterRating === "all" || comment.rating === parseInt(filterRating);
      const isLinked = !!comment.bookingId && typeof comment.bookingId !== "string";
      const bookingLinkMatch =
        filterBookingLink === "all" ||
        (filterBookingLink === "linked" ? isLinked : !isLinked);
      if (!statusMatch || !ratingMatch || !bookingLinkMatch) return false;
      if (!search.trim()) return true;
      const q = search.trim().toLowerCase();
      return (
        (comment.userName || "").toLowerCase().includes(q) ||
        (comment.userEmail || "").toLowerCase().includes(q) ||
        (comment.comment || comment.content || "").toLowerCase().includes(q) ||
        (comment.roomId?.roomName || comment.roomName || "").toLowerCase().includes(q)
      );
    });
  }, [comments, filterStatus, filterRating, filterBookingLink, search]);

  useEffect(() => { setPage(1); }, [filterStatus, filterRating, filterBookingLink, search]);

  const totalPages = Math.ceil(filteredComments.length / PAGE_SIZE);
  const pageComments = useMemo(
    () => filteredComments.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    [filteredComments, page]
  );

  // Render stars
  const renderStars = (rating: number): string => {
    return "⭐".repeat(rating) + "☆".repeat(5 - rating);
  };

  const formatBookingRange = (booking: CommentBookingRef): string => {
    const fmt = (d?: string) => (d ? new Date(d).toLocaleDateString("vi-VN") : "");
    const inPart = [fmt(booking.checkInDate), booking.checkInTime].filter(Boolean).join(" ");
    const outPart = [fmt(booking.checkOutDate), booking.checkOutTime].filter(Boolean).join(" ");
    return `${inPart} → ${outPart}`;
  };

  const bookingStatusLabel: Record<string, string> = {
    pending: "Chờ duyệt",
    confirmed: "Đã xác nhận",
    completed: "Đã hoàn thành",
    cancelled: "Đã hủy",
  };

  // Hiển thị thông tin đơn đặt phòng gắn với bình luận (hoặc cảnh báo nếu
  // bình luận cũ chưa liên kết đơn — tạo trước khi ràng buộc này có hiệu lực)
  const renderBookingRef = (comment: Comment): React.ReactElement => {
    const booking = comment.bookingId;
    if (comment.source && comment.source !== "site") {
      return (
        <span className="text-xs px-2 py-1 rounded bg-emerald-50 text-emerald-700">
          💬 Nhập từ {SOURCE_LABEL[comment.source] || comment.source}
        </span>
      );
    }
    if (!booking || typeof booking === "string") {
      return (
        <span className="text-xs px-2 py-1 rounded bg-gray-100 text-gray-500">
          ⚠️ Chưa liên kết đơn đặt phòng
        </span>
      );
    }
    return (
      <span className="text-xs px-2 py-1 rounded bg-blue-50 text-blue-700">
        🗓️ {formatBookingRange(booking)}
        {booking.status ? ` · ${bookingStatusLabel[booking.status] || booking.status}` : ""}
      </span>
    );
  };

  // Get status badge
  const getStatusBadge = (status: string): React.ReactElement => {
    const badges: Record<string, { color: string; text: string }> = {
      approved: { color: "bg-green-100 text-green-800", text: "✓ Đã duyệt" },
      pending: { color: "bg-yellow-100 text-yellow-800", text: "⏳ Chờ duyệt" },
      rejected: { color: "bg-red-100 text-red-800", text: "✕ Từ chối" },
    };
    const badge = badges[status] || badges.pending;
    return (
      <span
        className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${badge.color}`}
      >
        {badge.text}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500 text-lg">Đang tải bình luận...</div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">
            Quản lý bình luận
          </h1>
          <p className="text-gray-600 mt-1">
            Kiểm duyệt và quản lý đánh giá của khách hàng
          </p>
        </div>
        <button
          onClick={openImport}
          className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 text-sm font-medium"
        >
          + Thêm đánh giá từ Zalo / nguồn khác
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Tổng bình luận</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">
                {comments.length}
              </p>
            </div>
            <div className="p-3 bg-blue-100 rounded-lg">
              <span className="text-3xl">💬</span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Đã duyệt</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">
                {comments.filter((c) => c.status === "approved").length}
              </p>
            </div>
            <div className="p-3 bg-green-100 rounded-lg">
              <span className="text-3xl">✓</span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Chờ duyệt</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">
                {comments.filter((c) => c.status === "pending").length}
              </p>
            </div>
            <div className="p-3 bg-yellow-100 rounded-lg">
              <span className="text-3xl">⏳</span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Đánh giá TB</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">
                {comments.length > 0
                  ? (
                      comments.reduce((sum, c) => sum + (c.rating || 0), 0) /
                      comments.length
                    ).toFixed(1)
                  : "0"}{" "}
                ⭐
              </p>
            </div>
            <div className="p-3 bg-coffee-100 rounded-lg">
              <span className="text-3xl">📊</span>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
        <div className="flex flex-wrap gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Trạng thái
            </label>
            <select
              value={filterStatus}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setFilterStatus(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-coffee-500"
            >
              <option value="all">Tất cả</option>
              <option value="approved">Đã duyệt</option>
              <option value="pending">Chờ duyệt</option>
              <option value="rejected">Từ chối</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Đánh giá
            </label>
            <select
              value={filterRating}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setFilterRating(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-coffee-500"
            >
              <option value="all">Tất cả</option>
              <option value="5">5 sao ⭐⭐⭐⭐⭐</option>
              <option value="4">4 sao ⭐⭐⭐⭐</option>
              <option value="3">3 sao ⭐⭐⭐</option>
              <option value="2">2 sao ⭐⭐</option>
              <option value="1">1 sao ⭐</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Đơn đặt phòng
            </label>
            <select
              value={filterBookingLink}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setFilterBookingLink(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-coffee-500"
            >
              <option value="all">Tất cả</option>
              <option value="linked">Có liên kết đơn</option>
              <option value="unlinked">Chưa liên kết</option>
            </select>
          </div>

          <div className="flex-1 min-w-[220px]">
            <label className="block text-sm font-medium text-gray-700 mb-2">Tìm kiếm</label>
            <div className="relative">
              <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Tên, email, nội dung, phòng..."
                className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-coffee-500"
              />
            </div>
          </div>

          <div className="self-end text-sm text-gray-500 pb-2">
            {filteredComments.length} bình luận
          </div>
        </div>
      </div>

      {/* Comments List */}
      <div className="space-y-4">
        {pageComments.map((comment) => (
          <div
            key={comment._id}
            className="bg-white rounded-lg shadow-sm p-6 hover:shadow-md transition-shadow"
          >
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-start space-x-4">
                {/* Avatar */}
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 rounded-full bg-coffee-100 flex items-center justify-center">
                    <span className="text-coffee-600 font-bold text-lg">
                      {(comment.userName || "?").charAt(0).toUpperCase()}
                    </span>
                  </div>
                </div>

                {/* Content */}
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <h3 className="text-base font-bold text-gray-900">
                      {comment.userName || "Ẩn danh"}
                    </h3>
                    <span className="text-sm text-gray-500">
                      {comment.userEmail || ""}
                    </span>
                    {getStatusBadge(comment.status)}
                  </div>

                  <div className="flex items-center space-x-4 mb-3">
                    <span className="text-yellow-500 text-lg">
                      {renderStars(comment.rating || 0)}
                    </span>
                    <span className="text-sm text-gray-500">
                      📍 {comment.roomId?.roomName || comment.roomName || "N/A"}
                    </span>
                    <span className="text-sm text-gray-400">
                      {comment.createdAt ? new Date(comment.createdAt).toLocaleDateString("vi-VN") : ""}
                    </span>
                  </div>

                  <div className="mb-3">{renderBookingRef(comment)}</div>

                  <p className="text-gray-700 mb-3">{comment.comment || comment.content || ""}</p>

                  {/* Images */}
                  {comment.images && comment.images.length > 0 && (
                    <div className="flex space-x-2 mb-3">
                      {comment.images.map((img, idx) => (
                        <img
                          key={idx}
                          src={img}
                          alt={`Review ${idx}`}
                          className="w-20 h-20 object-cover rounded-lg cursor-pointer hover:opacity-80"
                          onClick={() => window.open(img, "_blank")}
                        />
                      ))}
                    </div>
                  )}

                  {/* Admin Reply */}
                  {comment.adminReply && (
                    <div className="mt-3 p-3 bg-coffee-50 border-l-4 border-coffee-400 rounded">
                      <p className="text-sm font-semibold text-coffee-900 mb-1">
                        💬 Phản hồi từ Admin:
                      </p>
                      <p className="text-sm text-coffee-800">
                        {comment.adminReply}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200">
              <button
                onClick={() => openDetail(comment)}
                className="px-4 py-2 text-sm bg-coffee-600 text-white rounded-lg hover:bg-coffee-700"
              >
                💬 Trả lời
              </button>

              {comment.status === "pending" && (
                <>
                  <button
                    onClick={() => updateStatus(comment._id, "approved")}
                    className="px-4 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700"
                  >
                    ✓ Duyệt
                  </button>
                  <button
                    onClick={() => updateStatus(comment._id, "rejected")}
                    className="px-4 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700"
                  >
                    ✕ Từ chối
                  </button>
                </>
              )}

              {isSuperAdmin && (
                <button
                  onClick={() => deleteComment(comment._id)}
                  className="px-4 py-2 text-sm bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300"
                >
                  🗑️ Xóa
                </button>
              )}
            </div>
          </div>
        ))}

        {/* Empty State */}
        {filteredComments.length === 0 && (
          <div className="bg-gray-50 rounded-lg p-12 text-center">
            <div className="text-6xl mb-4">💬</div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              Không có bình luận nào
            </h3>
            <p className="text-gray-600">
              Chưa có bình luận phù hợp với bộ lọc
            </p>
          </div>
        )}
      </div>

      <Pagination page={page} totalPages={totalPages} total={filteredComments.length} pageSize={PAGE_SIZE} onChange={setPage} />

      {/* Detail Modal */}
      {showDetailModal && selectedComment && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              {/* Modal Header */}
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900">
                  Chi tiết bình luận
                </h2>
                <button
                  onClick={() => setShowDetailModal(false)}
                  className="text-gray-400 hover:text-gray-600 text-2xl"
                >
                  ✕
                </button>
              </div>

              {/* Comment Info */}
              <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center space-x-3 mb-3">
                  <div className="w-12 h-12 rounded-full bg-coffee-100 flex items-center justify-center">
                    <span className="text-coffee-600 font-bold text-lg">
                      {(selectedComment.userName || "?").charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900">
                      {selectedComment.userName || "Ẩn danh"}
                    </h3>
                    <p className="text-sm text-gray-600">
                      {selectedComment.userEmail || ""}
                    </p>
                  </div>
                </div>

                <div className="mb-2">
                  <span className="text-yellow-500 text-xl">
                    {renderStars(selectedComment.rating || 0)}
                  </span>
                </div>

                <p className="text-gray-700 mb-2">{selectedComment.comment || selectedComment.content || ""}</p>

                <p className="text-sm text-gray-500 mb-2">
                  📍 {selectedComment.roomId?.roomName || selectedComment.roomName || "N/A"} •{" "}
                  {selectedComment.createdAt ? new Date(selectedComment.createdAt).toLocaleDateString("vi-VN") : ""}
                </p>

                {renderBookingRef(selectedComment)}

                {/* Images */}
                {selectedComment.images &&
                  selectedComment.images.length > 0 && (
                    <div className="flex space-x-2 mt-3">
                      {selectedComment.images.map((img, idx) => (
                        <img
                          key={idx}
                          src={img}
                          alt={`Review ${idx}`}
                          className="w-24 h-24 object-cover rounded-lg"
                        />
                      ))}
                    </div>
                  )}
              </div>

              {/* Reply Form */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Phản hồi của Admin
                </label>
                <textarea
                  rows={4}
                  value={replyText}
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setReplyText(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-coffee-500 focus:border-transparent"
                  placeholder="Nhập phản hồi của bạn..."
                />
              </div>

              {/* Buttons */}
              <div className="flex space-x-3 mt-6">
                <button
                  onClick={saveReply}
                  className="flex-1 bg-coffee-600 text-white py-3 px-4 rounded-lg hover:bg-coffee-700 font-medium"
                >
                  💾 Lưu phản hồi
                </button>
                <button
                  onClick={() => setShowDetailModal(false)}
                  className="flex-1 bg-gray-200 text-gray-800 py-3 px-4 rounded-lg hover:bg-gray-300 font-medium"
                >
                  Hủy
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showImport && (
        <div
          className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4"
          onClick={() => !importing && setShowImport(false)}
        >
          <div
            className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-xl font-bold text-gray-900">Thêm đánh giá từ nguồn khác</h2>
            <p className="text-sm text-gray-500 mt-1 mb-4">
              Chỉ nhập lời khen <b>thật</b> của khách (vd tin nhắn Zalo) và nên xin phép khách. Trên web sẽ hiện nhãn
              “Qua Zalo” để người xem biết nguồn; không cần email hay đơn đặt phòng.
            </p>

            <div className="mb-4 inline-flex rounded-lg border border-gray-200 bg-gray-50 p-0.5 text-sm">
              {[false, true].map((m) => (
                <button
                  key={String(m)}
                  type="button"
                  onClick={() => setBulkMode(m)}
                  className={`px-3 py-1.5 rounded-md transition-colors ${bulkMode === m ? "bg-white shadow-sm font-medium text-gray-900" : "text-gray-500 hover:text-gray-800"}`}
                >
                  {m ? "Nhập nhiều dòng" : "Nhập từng cái"}
                </button>
              ))}
            </div>

            {!bulkMode && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phòng</label>
                <select
                  value={importForm.roomId}
                  onChange={(e) => setImportForm({ ...importForm, roomId: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                >
                  <option value="">— Chọn phòng —</option>
                  {importRooms.map((r) => (
                    <option key={r._id} value={r._id}>{r.name}</option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tên hiển thị</label>
                  <input
                    value={importForm.userName}
                    maxLength={60}
                    placeholder="vd: Chị Lan"
                    onChange={(e) => setImportForm({ ...importForm, userName: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Số sao</label>
                  <select
                    value={importForm.rating}
                    onChange={(e) => setImportForm({ ...importForm, rating: Number(e.target.value) })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  >
                    {[5, 4, 3, 2, 1].map((n) => (
                      <option key={n} value={n}>{"⭐".repeat(n)}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nguồn</label>
                  <select
                    value={importForm.source}
                    onChange={(e) => setImportForm({ ...importForm, source: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  >
                    {Object.entries(SOURCE_LABEL).map(([k, v]) => (
                      <option key={k} value={k}>{v}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Ngày khách khen (tuỳ chọn)</label>
                  <input
                    type="date"
                    value={importForm.date}
                    max={localDateStr()}
                    onChange={(e) => setImportForm({ ...importForm, date: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nội dung (giữ nguyên lời khách)</label>
                <textarea
                  rows={5}
                  value={importForm.comment}
                  maxLength={1500}
                  onChange={(e) => setImportForm({ ...importForm, comment: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                />
              </div>
            </div>
            )}

            {bulkMode && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phòng</label>
                  <select
                    value={importForm.roomId}
                    onChange={(e) => setImportForm({ ...importForm, roomId: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  >
                    <option value="">— Chọn phòng —</option>
                    {importRooms.map((r) => (
                      <option key={r._id} value={r._id}>{r.name}</option>
                    ))}
                  </select>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Nguồn</label>
                    <select
                      value={importForm.source}
                      onChange={(e) => setImportForm({ ...importForm, source: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    >
                      {Object.entries(SOURCE_LABEL).map(([k, v]) => (
                        <option key={k} value={k}>{v}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Số sao</label>
                    <select
                      value={importForm.rating}
                      onChange={(e) => setImportForm({ ...importForm, rating: Number(e.target.value) })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    >
                      {[5, 4, 3, 2, 1].map((n) => (
                        <option key={n} value={n}>{"⭐".repeat(n)}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Tên hiển thị</label>
                    <input
                      value={importForm.userName}
                      maxLength={60}
                      placeholder={ANON_NAME}
                      onChange={(e) => setImportForm({ ...importForm, userName: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nội dung — mỗi dòng một đánh giá (giữ nguyên lời khách)
                  </label>
                  <textarea
                    rows={8}
                    value={bulkText}
                    onChange={(e) => setBulkText(e.target.value)}
                    placeholder={"Phòng cute lắm, chắc chắn sẽ ghé lại\nChủ nhà nhiệt tình, phòng sạch sẽ"}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    {bulkValid.length} dòng hợp lệ
                    {bulkLines.length > bulkValid.length ? ` · ${bulkLines.length - bulkValid.length} dòng bị bỏ (dưới 10 ký tự)` : ""}
                    . Để trống tên thì hiện “{ANON_NAME}”, không cần tên hay ảnh của khách.
                  </p>
                </div>
              </div>
            )}

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setShowImport(false)}
                disabled={importing}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Huỷ
              </button>
              <button
                onClick={bulkMode ? submitBulk : submitImport}
                disabled={importing}
                className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50"
              >
                {importing ? "Đang lưu..." : "Lưu đánh giá"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CommentsPage;
